import type {
  ProfileComparisonDevelopmentEffort,
  ProfileComparisonDevelopmentEffortItem,
  ProfileComparisonDimension,
  ProfileComparisonDimensionDelta,
  ProfileComparisonEffortLevel,
  ProfileComparisonGapBreakdown,
  ProfileComparisonResult,
  ProfileComparisonSummary,
  ProfileComparisonVerdict,
} from '../types/ProfileComparisonTypes';

interface ApiProfileComparisonSummary {
  profile_id?: string;
  overall_score?: number | null;
  seniority?: string | null;
  department?: string | null;
  target_role?: string | null;
}

interface ApiProfileComparisonVerdict {
  leading_profile_id?: string | null;
  dimensions_led?: number;
  is_tied?: boolean;
}

interface ApiProfileComparisonDimensionDelta {
  dimension?: string;
  score_a?: number | null;
  score_b?: number | null;
  delta_a?: number | null;
  delta_b?: number | null;
}

interface ApiProfileComparisonDevelopmentEffortItem {
  gap_count?: number;
  effort_level?: string;
}

interface ApiProfileComparisonPayload {
  profile_a?: ApiProfileComparisonSummary;
  profile_b?: ApiProfileComparisonSummary;
  role_mismatch?: boolean;
  verdict?: ApiProfileComparisonVerdict;
  dimension_deltas?: ApiProfileComparisonDimensionDelta[];
  gap_breakdown?: {
    only_profile_a?: string[];
    only_profile_b?: string[];
    shared?: string[];
  };
  development_effort?: {
    profile_a?: ApiProfileComparisonDevelopmentEffortItem;
    profile_b?: ApiProfileComparisonDevelopmentEffortItem;
  };
}

const mapSummary = (raw?: ApiProfileComparisonSummary): ProfileComparisonSummary => ({
  profileId: raw?.profile_id ?? '',
  overallScore: raw?.overall_score ?? null,
  seniority: raw?.seniority ?? null,
  department: raw?.department ?? null,
  targetRole: raw?.target_role ?? null,
});

// BE dimension_deltas already ships real v2 dimension names (core_competencies, hard_skills,
// soft_skills, responsibilities); legacy names are tolerated during the soak period.
const DIMENSION_MAP: Record<string, ProfileComparisonDimension> = {
  overall: 'overall',
  core_competencies: 'coreCompetencies',
  hard_skills: 'hardSkills',
  tools: 'tools',
  domain_knowledge: 'domainKnowledge',
  soft_skills: 'softSkills',
  responsibilities: 'responsibilities',
  competencies: 'coreCompetencies',
  knowledge: 'hardSkills',
};

const mapDimension = (raw: string | undefined): ProfileComparisonDimension => DIMENSION_MAP[raw ?? ''] ?? 'overall';

const mapVerdict = (raw?: ApiProfileComparisonVerdict): ProfileComparisonVerdict => ({
  leadingProfileId: raw?.leading_profile_id ?? null,
  dimensionsLed: raw?.dimensions_led ?? 0,
  isTied: raw?.is_tied ?? false,
});

const mapDimensionDelta = (raw: ApiProfileComparisonDimensionDelta): ProfileComparisonDimensionDelta => ({
  dimension: mapDimension(raw.dimension),
  scoreA: raw.score_a ?? null,
  scoreB: raw.score_b ?? null,
  deltaA: raw.delta_a ?? null,
  deltaB: raw.delta_b ?? null,
});

const mapGapBreakdown = (raw?: ApiProfileComparisonPayload['gap_breakdown']): ProfileComparisonGapBreakdown => ({
  onlyProfileA: raw?.only_profile_a ?? [],
  onlyProfileB: raw?.only_profile_b ?? [],
  shared: raw?.shared ?? [],
});

const mapEffortItem = (raw?: ApiProfileComparisonDevelopmentEffortItem): ProfileComparisonDevelopmentEffortItem => ({
  gapCount: raw?.gap_count ?? 0,
  effortLevel: (raw?.effort_level ?? 'low') as ProfileComparisonEffortLevel,
});

const mapDevelopmentEffort = (
  raw?: ApiProfileComparisonPayload['development_effort'],
): ProfileComparisonDevelopmentEffort => ({
  profileA: mapEffortItem(raw?.profile_a),
  profileB: mapEffortItem(raw?.profile_b),
});

export const mapProfileComparisonResponse = (raw: unknown): ProfileComparisonResult => {
  const payload = raw as ApiProfileComparisonPayload;

  return {
    profileA: mapSummary(payload.profile_a),
    profileB: mapSummary(payload.profile_b),
    roleMismatch: payload.role_mismatch ?? false,
    verdict: mapVerdict(payload.verdict),
    dimensionDeltas: (payload.dimension_deltas ?? []).map(mapDimensionDelta),
    gapBreakdown: mapGapBreakdown(payload.gap_breakdown),
    developmentEffort: mapDevelopmentEffort(payload.development_effort),
  };
};
